/**
 * Shared payment methods — client helpers (task #80).
 *
 * Cards are personal; their owner shares them per organization from the
 * wallet page. Seeing an org's shared cards needs billing.payment-methods.view,
 * paying with them needs billing.payment-methods.use — neither is in the
 * default admin role.
 */

import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface SharedPaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  ownerLabel: string | null;
  mine: boolean;
  /**
   * Whether this card can pay a RECURRING subscription for the org — true
   * only when the card's owner IS the org's billing profile (a pm can only
   * charge the customer holding it). Every shared card can still pay
   * one-off org top-ups. Absent on older server builds → treat as false.
   */
  subscriptionUsable?: boolean;
}

/** Cards shared with this org. Empty on permission-denied — the surfaces
 * simply don't show a shared-cards section to members without the view
 * permission. */
export async function listOrgSharedPaymentMethods(accountId: string): Promise<SharedPaymentMethod[]> {
  try {
    const fn = httpsCallable(getFunctions(getApp()), 'listOrgSharedPaymentMethods');
    const res = await fn({ accountId });
    const methods = (res.data as { methods?: SharedPaymentMethod[] })?.methods;
    return Array.isArray(methods) ? methods : [];
  } catch {
    return [];
  }
}

/** Replace the set of organizations this card is shared with (owner only —
 * the server verifies the pm belongs to the caller's Stripe customer). */
export async function setPaymentMethodOrgSharing(
  paymentMethodId: string,
  accountIds: string[]
): Promise<{ ok: boolean; error?: string }> {
  try {
    const fn = httpsCallable(getFunctions(getApp()), 'setPaymentMethodOrgSharing');
    await fn({ paymentMethodId, accountIds });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error)?.message || 'could not update sharing' };
  }
}
